import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../../components/global/layout";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import { toast } from "react-toastify";
import FormControl from "../../components/common/FormControl";
// import { MODULE_CONFIG, getPortalUrl } from "./shared";
import { MODULE_CONFIG, getPortalUrl, getTenantLogoUrl } from "./shared";
import {
  FiArrowLeft,
  FiSave,
  FiUploadCloud,
  FiCheckCircle,
  FiAlertCircle,
  FiLock,
  FiMail,
  FiRefreshCw,
  FiTrash2,
  FiX,
} from "react-icons/fi";
import { HiOutlineBuildingOffice2 } from "react-icons/hi2";

export default function TenantAddEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  // Form State
  const [formData, setFormData] = useState({
    company_name: "",
    subdomain: "",
    portal_title: "",
    support_email: "",
    contact_email: "",
    support_phone: "",
    contact_phone: "",
    primary_color: "#002B49",
    secondary_color: "#005A9C",
    accent_color: "#EAA812",
    logo: "",
    address: {
      street: "",
      city: "",
      state: "",
      country: "USA",
      zipCode: "",
    },
    // Admin credentials (Required on Add, hidden on Edit)
    adminFirstName: "",
    adminLastName: "",
    adminEmail: "",
    adminPassword: "",
    adminPhone: "",
  });

  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);

  // Subdomain validation state
  const [subdomainStatus, setSubdomainStatus] = useState({
    checking: false,
    checked: false,
    available: true,
    message: "",
  });

  const fileInputRef = useRef(null);

  // Fetch tenant details on Edit mode
  const fetchTenant = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    loader(true);

    try {
      const endpoint = MODULE_CONFIG.endpoints.detail(id);
      const res = await ApiClient.get(endpoint);

      if (res?.success && (res?.data || res?.data?.tenant)) {
        const data = res?.data?.tenant || res?.data?.data || res?.data;
        setFormData({
          company_name: data.company_name || data.name || "",
          subdomain: data.subdomain || "",
          portal_title: data.portal_title || "",
          support_email: data.support_email || "",
          contact_email: data.contact_email || "",
          support_phone: data.support_phone || "",
          contact_phone: data.contact_phone || "",
          primary_color: data.primary_color || "#002B49",
          secondary_color: data.secondary_color || "#005A9C",
          accent_color: data.accent_color || "#EAA812",
          // logo: data.logo || data.logoUrl || data.fileUrl || "",
          logo: data.logo_url || data.logo || data.logoUrl || data.fileUrl || "",
          address: {
            street: data.address?.street || "",
            city: data.address?.city || "",
            state: data.address?.state || "",
            country: data.address?.country || "USA",
            zipCode: data.address?.zipCode || "",
          },
          adminFirstName: "",
          adminLastName: "",
          adminEmail: "",
          adminPassword: "",
          adminPhone: "",
        });
      } else {
        toast.error("Unable to load tenant details.");
      }
    } catch (err) {
      console.error("Failed to load tenant:", err);
      toast.error("Failed to load tenant details.");
    } finally {
      setLoading(false);
      loader(false);
    }
  }, [id]);

  useEffect(() => {
    if (isEdit) {
      fetchTenant();
    }
  }, [isEdit, fetchTenant]);

  // Real-time debounced subdomain availability check (on Add mode only)
  useEffect(() => {
    if (isEdit) return;
    const cleanSub = formData.subdomain.trim().toLowerCase();

    if (!cleanSub) {
      setSubdomainStatus({
        checking: false,
        checked: false,
        available: true,
        message: "",
      });
      return;
    }

    if (!/^[a-z0-9-]+$/.test(cleanSub)) {
      setSubdomainStatus({
        checking: false,
        checked: true,
        available: false,
        message: "Only lowercase letters, numbers, and hyphens are allowed.",
      });
      return;
    }

    setSubdomainStatus((prev) => ({ ...prev, checking: true }));

    const handler = setTimeout(async () => {
      try {
        const endpoint = `${MODULE_CONFIG.endpoints.checkSubdomain}?subdomain=${encodeURIComponent(
          cleanSub
        )}`;
        const res = await ApiClient.get(endpoint);

        if (res?.success && res?.data) {
          setSubdomainStatus({
            checking: false,
            checked: true,
            available: Boolean(res.data.available),
            message:
              res.data.reason ||
              (res.data.available
                ? "Subdomain is available."
                : "Subdomain is taken."),
          });
        } else {
          setSubdomainStatus({
            checking: false,
            checked: true,
            available: false,
            message: res?.message || "Subdomain is not available.",
          });
        }
      } catch (err) {
        setSubdomainStatus({
          checking: false,
          checked: true,
          available: false,
          message: "Unable to verify subdomain availability.",
        });
      }
    }, 350);

    return () => clearTimeout(handler);
  }, [formData.subdomain, isEdit]);

  // Input change handler
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Address change handler
  const handleAddressChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value,
      },
    }));
  };

  // Generate strong random password
  const handleGeneratePassword = () => {
    const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
    const lower = "abcdefghijkmnopqrstuvwxyz";
    const numbers = "23456789";
    const symbols = "@#$!%*";
    const all = upper + lower + numbers + symbols;

    let pwd = "";
    pwd += upper[Math.floor(Math.random() * upper.length)];
    pwd += lower[Math.floor(Math.random() * lower.length)];
    pwd += numbers[Math.floor(Math.random() * numbers.length)];
    pwd += symbols[Math.floor(Math.random() * symbols.length)];

    for (let i = 4; i < 12; i++) {
      pwd += all[Math.floor(Math.random() * all.length)];
    }

    pwd = pwd
      .split("")
      .sort(() => 0.5 - Math.random())
      .join("");

    handleInputChange("adminPassword", pwd);
    navigator.clipboard.writeText(pwd);
    toast.success("Strong password generated and copied to clipboard!");
  };

  // Upload Logo handler (Unpacks res.data.fileUrl properly)
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file (PNG, JPG, SVG).");
      return;
    }

    setLogoUploading(true);
    loader(true);

    try {
      const res = await ApiClient.postFormData(MODULE_CONFIG.endpoints.uploadLogo, {
        file: file,
      });

      if (res?.success) {
        let uploadedUrl =
          res?.data?.fileUrl ||
          res?.fileUrl ||
          res?.data?.filePath ||
          res?.filePath ||
          res?.data?.url ||
          res?.location;

        if (uploadedUrl && typeof uploadedUrl === "string") {
          if (window.location.protocol === "https:" && uploadedUrl.startsWith("http://")) {
            uploadedUrl = uploadedUrl.replace("http://", "https://");
          }
          setFormData((prev) => ({ ...prev, logo: uploadedUrl }));
          toast.success("Logo uploaded successfully.");
        } else {
          toast.error("Unable to get uploaded logo URL.");
        }
      } else {
        toast.error(res?.message || "Failed to upload logo.");
      }
    } catch (err) {
      console.error("Logo upload error:", err);
      toast.error("Failed to upload logo.");
    } finally {
      setLogoUploading(false);
      loader(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Submit Handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.company_name.trim()) {
      toast.error("Please enter the company name.");
      return;
    }

    if (!isEdit && !formData.subdomain.trim()) {
      toast.error("Please enter a subdomain.");
      return;
    }

    if (!isEdit && !subdomainStatus.available) {
      toast.error(subdomainStatus.message || "The specified subdomain is not available.");
      return;
    }

    if (!isEdit) {
      if (!formData.adminEmail.trim()) {
        toast.error("Please provide a primary tenant admin email.");
        return;
      }
      if (!formData.adminPassword || formData.adminPassword.length < 6) {
        toast.error("Password must be at least 6 characters.");
        return;
      }
    }

    setSubmitting(true);
    loader(true);

    try {
      if (isEdit) {
        // Edit payload
        const updatePayload = {
          company_name: formData.company_name.trim(),
          portal_title: formData.portal_title.trim(),
          primary_color: formData.primary_color || "#002B49",
          secondary_color: formData.secondary_color || "#005A9C",
          accent_color: formData.accent_color || "#EAA812",
          support_email: formData.support_email.trim(),
          contact_email: formData.contact_email.trim(),
          support_phone: formData.support_phone.trim(),
          contact_phone: formData.contact_phone.trim(),
          address: formData.address,
        };
        if (formData.logo) {
          // updatePayload.logo = formData.logo;
          updatePayload.logo = formData.logo;
          updatePayload.logo_url = formData.logo;
        }

        const endpoint = MODULE_CONFIG.endpoints.update(id);
        const res = await ApiClient.put(endpoint, updatePayload);

        if (res?.success) {
          toast.success("Tenant organization updated successfully.");
          navigate(`${MODULE_CONFIG.routeBase}/detail/${id}`);
        } else {
          toast.error(res?.message || "Failed to update tenant.");
        }
      } else {
        // Create payload
        const cleanSub = formData.subdomain.toLowerCase().trim().replace(/[^a-z0-9-]/g, "");
        const createPayload = {
          company_name: formData.company_name.trim(),
          subdomain: cleanSub,
          portal_title: formData.portal_title.trim() || formData.company_name.trim(),
          support_email: formData.support_email.trim() || formData.adminEmail.trim(),
          contact_email: formData.contact_email.trim() || formData.adminEmail.trim(),
          support_phone: formData.support_phone.trim() || formData.adminPhone.trim(),
          contact_phone: formData.contact_phone.trim() || formData.adminPhone.trim(),
          address: formData.address,
          primary_color: formData.primary_color || "#002B49",
          secondary_color: formData.secondary_color || "#005A9C",
          accent_color: formData.accent_color || "#EAA812",
          adminFirstName: formData.adminFirstName.trim(),
          adminLastName: formData.adminLastName.trim(),
          adminEmail: formData.adminEmail.trim(),
          adminPassword: formData.adminPassword,
          adminPhone: formData.adminPhone.trim(),
        };
        if (formData.logo) {
          // createPayload.logo = formData.logo;
          createPayload.logo = formData.logo;
          createPayload.logo_url = formData.logo;
        }

        const endpoint = MODULE_CONFIG.endpoints.add;
        const res = await ApiClient.post(endpoint, createPayload);

        if (res?.success) {
          toast.success("Tenant organization onboarded successfully!");
          const newId = res?.data?._id || res?.data?.id || res?.id;
          if (newId) {
            navigate(`${MODULE_CONFIG.routeBase}/detail/${newId}`);
          } else {
            navigate(MODULE_CONFIG.routeBase);
          }
        } else {
          toast.error(res?.message || "Failed to create tenant organization.");
        }
      }
    } catch (err) {
      console.error("Tenant submit error:", err);
      toast.error("Failed to save tenant organization.");
    } finally {
      setSubmitting(false);
      loader(false);
    }
  };

  const cleanSubdomain = formData.subdomain.toLowerCase().trim().replace(/[^a-z0-9-]/g, "");
  const currentPortalUrl = getPortalUrl(cleanSubdomain);

  return (
    <Layout>
      <div className="w-full max-w-full space-y-5 pb-16 antialiased">
        {/* ======================================================== */}
        {/* 1. TOP HEADER & BREADCRUMB ACTIONS                       */}
        {/* ======================================================== */}
        <div className="bg-white rounded-2xl p-4 sm:p-5 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.04)] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(MODULE_CONFIG.routeBase)}
              className="p-2.5 rounded-xl bg-slate-200 hover:bg-slate-300 text-slate-900 border border-slate-300 transition-colors cursor-pointer shadow-xs shrink-0 flex items-center justify-center"
              title="Back to Tenants List"
            >
              <FiArrowLeft className="w-5 h-5 text-slate-900" />
            </button>
            <div className="space-y-0.5">
              <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
                {isEdit ? "Edit Tenant Organization" : "Onboard New Tenant"}
              </h1>
              <p className="text-xs font-bold text-slate-700">
                {isEdit
                  ? "Update company metadata, portal title, logo, and contact info"
                  : "Register a multi-tenant client organization, custom subdomain, and primary admin account"}
              </p>
            </div>
          </div>

          {/* <div className="flex items-center gap-2.5 shrink-0 self-stretch sm:self-auto justify-end"> */}
            {/* <button
              type="button"
              onClick={() => navigate(MODULE_CONFIG.routeBase)}
              className="px-4 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-300 text-xs font-bold transition-all cursor-pointer shadow-xs"
            >
              Cancel
            </button> */}
            {/* Top button commented out as it has the same functionality as the bottom submit button */}
            {/*
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="inline-flex items-center justify-center gap-1.5 px-5 h-10 rounded-xl bg-[#115B9D] hover:bg-[#0e4b82] text-white text-xs font-bold transition-all shadow-md shadow-[#115B9D]/25 cursor-pointer active:scale-[0.98]"
            >
              <FiSave className="w-4 h-4" />
              <span>{isEdit ? "Save Changes" : "Create Tenant"}</span>
            </button>
            */}
          {/* </div> */}
        </div>

        {/* ======================================================== */}
        {/* 2. FORM BODY USING PLATFORM FORMCONTROL                  */}
        {/* ======================================================== */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* ROW 1: Company Profile & Subdomain (Left) + Contact Info (Right) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
            {/* Section 1: Company Profile & Subdomain */}
            <div className="bg-white rounded-2xl p-5 sm:p-6 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.04)] space-y-4 flex flex-col justify-between">
              <div className="space-y-4">
                <div className="flex items-center gap-2.5 border-b border-slate-200 pb-3">
                  <div className="w-7 h-7 rounded-lg bg-blue-50 text-[#115B9D] border border-blue-200 flex items-center justify-center font-bold">
                    <HiOutlineBuildingOffice2 className="w-4 h-4" />
                  </div>
                  <h3 className="text-sm font-black text-slate-900 uppercase tracking-wide">
                    Company Profile & Subdomain
                  </h3>
                </div>

                <div className="space-y-4">
                  {/* Company Name */}
                  <FormControl
                    label="Company / Organization Name"
                    required
                    value={formData.company_name}
                    onChange={(val) => handleInputChange("company_name", val)}
                    placeholder="e.g. Apex Restaurant Supply"
                  />

                  {/* Subdomain Input with Real-time Check */}
                  <div>
                    <FormControl
                      label="Assigned Subdomain"
                      required={!isEdit}
                      disabled={isEdit}
                      value={formData.subdomain}
                      onChange={(val) =>
                        handleInputChange(
                          "subdomain",
                          (val || "").toLowerCase().replace(/[^a-z0-9-]/g, "")
                        )
                      }
                      placeholder="e.g. apex"
                      error={
                        !isEdit && subdomainStatus.checked && !subdomainStatus.available
                          ? subdomainStatus.message
                          : null
                      }
                    />

                    {/* Subdomain live status pill */}
                    {!isEdit && formData.subdomain && (
                      <div className="mt-1.5 flex items-center gap-1.5 text-xs">
                        {subdomainStatus.checking ? (
                          <span className="text-slate-600 font-medium">
                            Checking availability...
                          </span>
                        ) : subdomainStatus.checked && subdomainStatus.available ? (
                          <div className="flex items-center gap-1 text-emerald-800 font-bold">
                            <FiCheckCircle className="w-3.5 h-3.5 text-emerald-700" />
                            <span>Available: {currentPortalUrl}</span>
                          </div>
                        ) : subdomainStatus.checked && !subdomainStatus.available ? (
                          <div className="flex items-center gap-1 text-rose-800 font-bold">
                            <FiAlertCircle className="w-3.5 h-3.5 text-rose-700" />
                            <span>{subdomainStatus.message}</span>
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>

                  {/* Portal Title */}
                  <FormControl
                    label="Portal Display Title"
                    value={formData.portal_title}
                    onChange={(val) => handleInputChange("portal_title", val)}
                    placeholder="e.g. Apex Commercial Equipment Portal"
                  />
                </div>
              </div>
            </div>

            {/* Section 2: Contact Info */}
            <div className="bg-white rounded-2xl p-5 sm:p-6 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.04)] space-y-4 flex flex-col justify-between">
              <div className="space-y-4">
                <div className="flex items-center gap-2.5 border-b border-slate-200 pb-3">
                  <div className="w-7 h-7 rounded-lg bg-emerald-50 text-emerald-800 border border-emerald-200 flex items-center justify-center font-bold">
                    <FiMail className="w-4 h-4" />
                  </div>
                  <h3 className="text-sm font-black text-slate-900 uppercase tracking-wide">
                    Communications & Support
                  </h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormControl
                    label="Contact Email"
                    type="email"
                    value={formData.contact_email}
                    onChange={(val) => handleInputChange("contact_email", val)}
                    placeholder="contact@tenant.com"
                  />

                  <FormControl
                    label="Support Email"
                    type="email"
                    value={formData.support_email}
                    onChange={(val) => handleInputChange("support_email", val)}
                    placeholder="support@tenant.com"
                  />

                  <FormControl
                    label="Contact Phone"
                    type="phone"
                    value={formData.contact_phone}
                    onChange={(val) => handleInputChange("contact_phone", val)}
                    placeholder="+1 (800) 555-0199"
                  />

                  <FormControl
                    label="Support Phone"
                    type="phone"
                    value={formData.support_phone}
                    onChange={(val) => handleInputChange("support_phone", val)}
                    placeholder="+1 (800) 555-0199"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ROW 2: Physical Headquarters Address & Official Organization Logo (Adjusted in One Row) */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
            {/* Physical Headquarters Address (High Space: 7 cols) */}
            <div className="lg:col-span-7 bg-white rounded-2xl p-5 sm:p-6 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.04)] flex flex-col justify-between space-y-4">
              <div className="space-y-4">
                <div className="flex items-center gap-2.5 border-b border-slate-200 pb-3">
                  <div className="w-7 h-7 rounded-lg bg-amber-50 text-amber-800 border border-amber-200 flex items-center justify-center font-bold">
                    <HiOutlineBuildingOffice2 className="w-4 h-4" />
                  </div>
                  <h3 className="text-sm font-black text-slate-900 uppercase tracking-wide">
                    Physical Headquarters Address
                  </h3>
                </div>

                <div className="space-y-4">
                  <FormControl
                    label="Street Address"
                    value={formData.address.street}
                    onChange={(val) => handleAddressChange("street", val)}
                    placeholder="e.g. 100 Commercial Way"
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormControl
                      label="City"
                      value={formData.address.city}
                      onChange={(val) => handleAddressChange("city", val)}
                      placeholder="Dallas"
                    />

                    <FormControl
                      label="State / Province"
                      value={formData.address.state}
                      onChange={(val) => handleAddressChange("state", val)}
                      placeholder="TX"
                    />

                    <FormControl
                      label="Postal / Zip Code"
                      value={formData.address.zipCode}
                      onChange={(val) => handleAddressChange("zipCode", val)}
                      placeholder="75001"
                    />

                    <FormControl
                      label="Country"
                      value={formData.address.country}
                      onChange={(val) => handleAddressChange("country", val)}
                      placeholder="USA"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Official Organization Logo (5 cols) */}
            <div className="lg:col-span-5 bg-white rounded-2xl p-5 sm:p-6 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.04)] flex flex-col justify-between space-y-4">
              <div className="space-y-4">
                <div className="flex items-center gap-2.5 border-b border-slate-200 pb-3">
                  <div className="w-7 h-7 rounded-lg bg-purple-50 text-purple-800 border border-purple-200 flex items-center justify-center font-bold">
                    <FiUploadCloud className="w-4 h-4" />
                  </div>
                  <h3 className="text-sm font-black text-slate-900 uppercase tracking-wide">
                    Official Organization Logo
                  </h3>
                </div>

                <div className="flex flex-col items-center justify-center p-4 rounded-xl bg-slate-50 border border-slate-200 text-center space-y-3">
                  {formData.logo ? (
                    <div className="relative group">
                      {/* <img
                        src={formData.logo}
                        alt="Logo Preview"
                        className="w-24 h-24 rounded-xl object-contain bg-white border border-slate-300 p-2 shadow-xs"
                      /> */}
                      <img
                        src={getTenantLogoUrl(formData.logo)}
                        alt="Logo Preview"
                        className="w-24 h-24 rounded-xl object-contain bg-white border border-slate-300 p-2 shadow-xs"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => handleInputChange("logo", "")}
                        className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-rose-600 text-white flex items-center justify-center shadow-md hover:bg-rose-700 transition-colors cursor-pointer"
                        title="Remove logo"
                      >
                        <FiX className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-24 h-24 rounded-xl bg-slate-200 border-2 border-dashed border-slate-400 flex flex-col items-center justify-center text-slate-500">
                      <FiUploadCloud className="w-7 h-7 mb-1 text-slate-400" />
                      <span className="text-[10px] font-bold uppercase text-slate-500">No Logo</span>
                    </div>
                  )}

                  <div className="space-y-1">
                    <h4 className="text-xs font-bold text-slate-900">
                      {formData.logo ? "Logo Image Uploaded" : "Upload Organization Logo"}
                    </h4>
                    <p className="text-[11px] font-medium text-slate-600">
                      PNG or SVG with transparent background
                    </p>
                    {formData.logo && (
                      <p className="text-[10px] font-mono text-slate-700 truncate max-w-[220px] mx-auto">
                        {formData.logo}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center justify-center gap-2 pt-1 flex-wrap">
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={logoUploading}
                      className="inline-flex items-center gap-1.5 px-4 h-10 rounded-xl bg-white hover:bg-slate-100 text-slate-800 border border-slate-300 text-xs font-bold transition-all shadow-xs cursor-pointer active:scale-[0.98]"
                    >
                      <FiUploadCloud className="w-4 h-4 text-[#115B9D]" />
                      <span>
                        {logoUploading
                          ? "Uploading..."
                          : formData.logo
                          ? "Replace Logo"
                          : "Upload Logo"}
                      </span>
                    </button>

                    {formData.logo && (
                      <button
                        type="button"
                        onClick={() => handleInputChange("logo", "")}
                        className="inline-flex items-center gap-1.5 px-3 h-10 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-800 border border-rose-300 text-xs font-bold transition-all shadow-xs cursor-pointer"
                      >
                        <FiTrash2 className="w-3.5 h-3.5" />
                        <span>Remove</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ROW 4: Initial Tenant Admin Credentials (Add Mode Only) */}
          {!isEdit && (
            <div className="w-full bg-white rounded-2xl p-5 sm:p-6 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.04)] space-y-4">
              <div className="flex items-center gap-2.5 border-b border-slate-200 pb-3">
                <div className="w-7 h-7 rounded-lg bg-indigo-50 text-indigo-800 border border-indigo-200 flex items-center justify-center font-bold">
                  <FiLock className="w-4 h-4" />
                </div>
                <h3 className="text-sm font-black text-slate-900 uppercase tracking-wide">
                  Initial Tenant Administrator Account
                </h3>
              </div>

              <p className="text-xs font-semibold text-slate-700">
                These credentials will be provisioned as the root organization admin for the new tenant portal.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <FormControl
                  label="Admin First Name"
                  value={formData.adminFirstName}
                  onChange={(val) => handleInputChange("adminFirstName", val)}
                  placeholder="e.g. John"
                />

                <FormControl
                  label="Admin Last Name"
                  value={formData.adminLastName}
                  onChange={(val) => handleInputChange("adminLastName", val)}
                  placeholder="e.g. Doe"
                />

                <FormControl
                  label="Admin Phone"
                  type="phone"
                  value={formData.adminPhone}
                  onChange={(val) => handleInputChange("adminPhone", val)}
                  placeholder="+1 (214) 555-0144"
                />

                <FormControl
                  label="Admin Login Email"
                  type="email"
                  required
                  value={formData.adminEmail}
                  onChange={(val) => handleInputChange("adminEmail", val)}
                  placeholder="admin.apex@yopmail.com"
                />

                {/* Password with Generate Password Action */}
                <div className="sm:col-span-2">
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-semibold text-slate-800">
                      Admin Login Password <span className="text-red-500">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={handleGeneratePassword}
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-[#115B9D] hover:text-[#0e4b82] transition-colors cursor-pointer py-1 px-2.5 rounded-lg bg-blue-50 hover:bg-blue-100 border border-blue-200 shadow-2xs"
                      title="Generate a secure random password"
                    >
                      <FiRefreshCw className="w-3.5 h-3.5" />
                      <span>Generate Password</span>
                    </button>
                  </div>
                  <FormControl
                    type="password"
                    showEye={true}
                    required
                    value={formData.adminPassword}
                    onChange={(val) => handleInputChange("adminPassword", val)}
                    placeholder="Temporary secure password (min 6 characters)"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Bottom Actions */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => navigate(MODULE_CONFIG.routeBase)}
              className="px-5 h-11 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-300 text-xs font-bold transition-all cursor-pointer shadow-xs"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center justify-center gap-1.5 px-6 h-11 rounded-xl bg-[#115B9D] hover:bg-[#0e4b82] text-white text-xs font-bold transition-all shadow-md shadow-[#115B9D]/25 cursor-pointer active:scale-[0.98]"
            >
              <FiSave className="w-4 h-4" />
              <span>{isEdit ? "Update Organization" : "Register Tenant Organization"}</span>
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
